import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Comment } from '../../data-contract/comment';
import { useCommentsApi, useOptionalCommentsApi } from '../comments-api-context';
import type { CommentsScope } from '../comments-scope-context';
import { useCommentsScope, useOptionalCommentsScope } from '../comments-scope-context';

const UNSCOPED: CommentsScope = {
    architectureId: '',
    canCreateThreads: false,
    itemId: '',
    kind: 'job-profile',
    language: '',
    persistence: 'unavailable',
    requestedVersion: 0,
};

const UNSCOPED_COMMENTS_QUERY_KEY = ['comments', 'unscoped'] as const;

type UseCommentsQueryParams = {
    readonly enabled?: boolean;
};

export const useCommentsQuery = ({
    enabled = true,
}: UseCommentsQueryParams = {}): UseQueryResult<readonly Comment[], Error> => {
    const api = useOptionalCommentsApi();
    const scope = useOptionalCommentsScope();
    const { architectureId, itemId, language, version: vettedVersion } = scope ?? UNSCOPED;
    const queryVersion = vettedVersion ?? 0;

    return useQuery<readonly Comment[], Error>({
        enabled: enabled && !is.null(api) && !is.null(scope) && !is.undefined(vettedVersion),
        queryFn: async () => {
            if (is.null(api)) {
                throw new Error('comments api unavailable');
            }
            const result = await api.fetchers.fetchComments({
                architectureId,
                itemId,
                language,
                version: queryVersion,
            });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        queryKey: is.null(api)
            ? UNSCOPED_COMMENTS_QUERY_KEY
            : api.keys.list({ architectureId, itemId, version: queryVersion }, language),
        throwOnError: false,
    });
};

export const useAllLanguageCommentsQuery = (): UseQueryResult<readonly Comment[], Error> => {
    const { fetchers, keys } = useCommentsApi();
    const { architectureId, itemId, version } = useCommentsScope();
    const queryVersion = version ?? 0;

    return useQuery<readonly Comment[], Error>({
        enabled: !is.undefined(version),
        queryFn: async () => {
            const result = await fetchers.fetchComments({
                architectureId,
                itemId,
                version: queryVersion,
            });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        queryKey: keys.allLanguages({ architectureId, itemId, version: queryVersion }),
        throwOnError: false,
    });
};
